import ButtonWithIcon from '@/components/common/ButtonWithIcon';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { useKeyboardListener } from '@/components/common/hooks/useKeyboardListener';
import { ArrowDown } from '@/components/common/icons';
import InputField from '@/components/common/InputField';
import SlideAnimator, {
    FrameRenderer,
    GoBackFcn,
    GoToFcn,
    HideSlideupFcn,
    ShowSlideupFcn,
} from '@/components/common/SlideAnimator';
import {
    BigParagraph,
    LinkText,
    Subheader,
} from '@/components/common/Typography';
import useAutofocus from '@/components/common/useAutofocus';
import { useUserData } from '@/services/user';
import { FlexProps } from '@chakra-ui/react';
import { isDefined } from '@chakra-ui/utils';
import { assert } from '@common/errors';
import { Goal } from '@common/goals';
import { CurrentRateInfo } from '@common/graphql/generated';
import { useCurrentRateInfo } from '@common/react/rates';
import { UserInfo } from '@common/react/user';
import { DefaultGoalAccountOnboardingInfo } from '@common/types';
import { isEmpty, isNumber } from '@common/util/types';
import isEqual from 'lodash.isequal';
import {
    Context,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { GoalConfig, GoalData, GoalFieldConfig } from '../config/types';
import {
    getGoalCardIndexFromFieldName,
    getGoalDataFromExistingGoal,
    useUpdateGoal,
} from '../helpers';
import GoalCardLayout, { GoalCardLayoutProps } from './GoalCardLayout';

interface GoalCardStackContext<TGoal extends Goal> {
    goalId: string;
    config: GoalConfig<TGoal>;
    goalType: TGoal['type'];
    goalData: GoalData<TGoal>;
    confirmationButtonText: string;
    userInfo: UserInfo;
    activePartnershipId?: string | null | undefined;
    currentRateInfo: CurrentRateInfo;
    initialFrameIndex?: number;
    setGoalData: (data: GoalData<TGoal>) => void;
    updateField: (fieldName: string, fieldValue: unknown) => void;
    onClose: VoidFunction;
    onComplete?: (goal: TGoal) => void;
}
const GoalCardStackContext = createContext<GoalCardStackContext<Goal> | null>(
    null
);
const GoalCardStackProvider = <TGoal extends Goal>({
    children,
    value,
}: React.PropsWithChildren<{ value: GoalCardStackContext<TGoal> }>) => {
    return (
        <GoalCardStackContext.Provider
            value={value as unknown as GoalCardStackContext<Goal>}
        >
            {children}
        </GoalCardStackContext.Provider>
    );
};
const useGoalCardStackContext = <TGoal extends Goal>() => {
    const ctx = useContext<GoalCardStackContext<TGoal>>(
        GoalCardStackContext as unknown as Context<GoalCardStackContext<TGoal>>
    );
    assert(ctx, 'Context must be defined');
    return ctx;
};

interface GoalCardStackProps<TGoal extends Goal> {
    // Unique ID for this potential goal to prevent unnecessary rerenders
    goalId: string;
    config: GoalConfig<TGoal>;
    goalType: TGoal['type'];
    goal: TGoal;
    initialFrameIndex?: number;
    confirmationButtonText: string;
    onClose: VoidFunction;
    onComplete?: (goal: Goal) => void;
}

export const GoalCardStack = <TGoal extends Goal>({
    goalId,
    config,
    goal,
    goalType,
    initialFrameIndex,
    confirmationButtonText,
    onClose,
    onComplete,
}: GoalCardStackProps<TGoal>) => {
    const [goalData, setGoalData] = useState(getGoalDataFromExistingGoal(goal));
    const { userInfo, activePartnershipId } = useUserData();
    const { currentRateInfo } = useCurrentRateInfo();
    if (!currentRateInfo || !userInfo) return <></>;

    const updateField = (fieldName: string, fieldValue: unknown) => {
        const newGoalData = { ...goalData, [fieldName]: fieldValue };
        if (config.calculateDerivedGoalData) {
            // TODO: Prevent race conditions
            // Consider blocking user input until this function call resolves
            config
                .calculateDerivedGoalData({
                    oldGoalData: goalData,
                    newGoalData,
                    userInfo,
                    currentRateInfo,
                })
                .then((calculatedData) => {
                    if (!isEqual(goalData, calculatedData)) {
                        setGoalData(calculatedData);
                    }
                });
        } else {
            setGoalData(newGoalData);
        }
    };

    return (
        <GoalCardStackProvider
            value={{
                goalId,
                config,
                goalType,
                confirmationButtonText,
                goalData,
                userInfo,
                activePartnershipId,
                currentRateInfo,
                initialFrameIndex,
                setGoalData,
                updateField,
                onClose,
                onComplete,
            }}
        >
            <GoalCardStackForUpdate goal={goal} />
        </GoalCardStackProvider>
    );
};

const GoalCardStackForUpdate = <TGoal extends Goal>({
    goal,
}: {
    goal: TGoal;
}) => {
    const { config, goalData, userInfo, onClose, onComplete } =
        useGoalCardStackContext();

    const [isSubmitting, setSubmitting] = useState(false);
    const updateGoal = useUpdateGoal(goal);

    const onSubmit = ({ goTo }: { goTo: (index: number) => void }) => {
        if (isSubmitting) return;
        setSubmitting(true);

        const onError = (err: Error) => {
            console.error(err);
            setSubmitting(false);
        };
        const onCompleted = (goal: TGoal | null | undefined) => {
            setSubmitting(false);
            if (!goal) {
                console.error('Goal missing in response payload');
                return;
            }
            onClose();
            onComplete?.(goal);
            // reset to first step
            goTo(0);
        };

        const {
            name,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            shareStatus,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            isMine,
            targetDate,
            targetAmount,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            balance,
            riskScore,
            initialDepositPlan,
            transactionSchedulePlan,
            fundingAccountId,
            lastCompletedStep,
            partnerInitialDepositPlan,
            partnerTransactionSchedulePlan,
            investmentType,
            ...typeInfo
        } = goalData;

        const recommendedRiskScore =
            riskScore ??
            config.getRecommendedRiskScore({
                goalData,
                userInfo,
            }) ??
            undefined;

        const goalAccountOnboardingInfo: DefaultGoalAccountOnboardingInfo = {
            infoType: 'DEFAULT' as const,
            riskScore: recommendedRiskScore,
            initialDepositPlan,
            transactionSchedulePlan,
            partnerInitialDepositPlan,
            partnerTransactionSchedulePlan,
            fundingAccountId,
            lastCompletedStep,
            investmentType,
        };

        // Create
        updateGoal(
            {
                id: goal.id,
                name,
                targetDate,
                targetAmount,
                typeInfo,
            },
            {
                info: goalAccountOnboardingInfo,
            }
        )
            .then((goal) =>
                onCompleted(goal as unknown as TGoal | null | undefined)
            )
            .catch(onError);
    };

    return <GoalCardStackFrames onSubmit={onSubmit} />;
};

interface GoalCardStackFramesProps {
    isSubmitting?: boolean;
    onSubmit: ({ goTo }: { goTo: (index: number) => void }) => void;
}
const GoalCardStackFrames = ({
    isSubmitting,
    onSubmit,
}: GoalCardStackFramesProps) => {
    const {
        goalId,
        config,
        goalType,
        confirmationButtonText,
        goalData,
        currentRateInfo,
        userInfo,
        updateField,
        initialFrameIndex,
        onClose,
    } = useGoalCardStackContext();

    const confirmationFrame: FrameRenderer = {
        id: 'confirmation',
        render: ({ goBack, goTo, showSlideup }) => {
            return (
                <GoalCardLayout
                    key={goalId + 'confirmation'}
                    title={goalData.name}
                    type={goalType}
                    onBack={
                        config.inputSteps.length === 0 || isSubmitting
                            ? undefined
                            : () => {
                                  config.onConfirmationBack
                                      ? config.onConfirmationBack({
                                            goalData,
                                            goBack,
                                        })
                                      : goBack();
                              }
                    }
                    nextButtonText={confirmationButtonText}
                    isNextLoading={isSubmitting}
                    onNext={() => onSubmit({ goTo })}
                    onClose={onClose}
                >
                    <BigParagraph>
                        {config.getGoalStatementContent({
                            goalData,
                            userInfo,
                            currentRateInfo,
                            onGoalStatementClick: (fieldName) => {
                                const idx = getGoalCardIndexFromFieldName(
                                    config.inputSteps,
                                    fieldName
                                );
                                if (isNumber(idx)) {
                                    goTo(idx);
                                }
                            },
                        })}
                    </BigParagraph>
                    {config.getAssumptionsContent && (
                        <LinkText
                            fontSize="sm"
                            fontWeight="medium"
                            mt={3}
                            onClick={() => {
                                showSlideup('assumptions');
                            }}
                        >
                            See assumptions
                        </LinkText>
                    )}
                    {config.getAdditionalConfirmationContent?.()}
                </GoalCardLayout>
            );
        },
    };

    const assumptionsFrame: FrameRenderer = {
        id: 'assumptions',
        render: ({ hideSlideup, goTo }) => {
            return (
                <AssumptionsCard
                    key={goalId + 'assumptions'}
                    content={({ goalData }) => (
                        <>
                            {config.getAssumptionsContent?.({
                                goalData,
                                userInfo,
                                currentRateInfo,
                            })}
                        </>
                    )}
                    goalData={goalData}
                    goTo={goTo}
                    hideSlideup={hideSlideup}
                />
            );
        },
    };

    const inputFrames: FrameRenderer[] = [
        ...config.inputSteps.map((step) => {
            const title =
                typeof step.title === 'function'
                    ? step.title({ goalData, userInfo }) ?? ''
                    : step.title ?? '';
            const subtitle =
                typeof step.subtitle === 'function'
                    ? step.subtitle({ goalData, userInfo })
                    : step.subtitle;

            const frame: FrameRenderer = {
                id: step.id,
                render: ({ goBack, goForward, showSlideup, isFirst }) => {
                    const bottomContent =
                        typeof step.bottomContent === 'function'
                            ? step.bottomContent({
                                  goalData,
                                  userInfo,
                                  goBack,
                                  showSlideup,
                              })
                            : step.bottomContent;
                    const id = goalId + step.id;
                    return (
                        <InputFieldCard
                            key={id}
                            id={id}
                            title={title}
                            subtitle={subtitle}
                            bottomContent={bottomContent}
                            goalData={goalData}
                            updateField={updateField}
                            type={goalType}
                            onNext={
                                step.onNext
                                    ? () =>
                                          step.onNext?.({
                                              goalData,
                                              goForward,
                                          })
                                    : goForward
                            }
                            onBack={
                                step.onBack
                                    ? () =>
                                          step.onBack?.({
                                              goalData,
                                              goBack,
                                          })
                                    : goBack
                            }
                            onClose={onClose}
                            goBack={goBack}
                            showSlideup={showSlideup}
                            fieldConfigs={step.fieldConfigs}
                            fieldContainerFlexProps={
                                step.fieldContainerFlexProps
                            }
                            isFirstCard={isFirst}
                            isNextButtonDisabled={step.isNextButtonDisabled}
                        />
                    );
                },
            };
            return frame;
        }),
    ];

    const frames: FrameRenderer[] = [...inputFrames, confirmationFrame];

    const assumptionsFrames: FrameRenderer[] = [
        ...(config.assumptions ?? []).map(
            ({ id, title, content }): FrameRenderer => ({
                id,
                render: ({ goTo, hideSlideup }) => (
                    <AssumptionsCard
                        key={goalId + id}
                        title={title}
                        content={content}
                        goalData={goalData}
                        goTo={goTo}
                        hideSlideup={hideSlideup}
                    />
                ),
            })
        ),
        assumptionsFrame,
    ];

    return (
        <SlideAnimator
            rounded="md"
            shadow="md"
            w="full"
            h="full"
            bg="white"
            frames={frames}
            slideupFrames={assumptionsFrames}
            initialFrameIndex={initialFrameIndex}
        />
    );
};

interface InputFieldCardProps<TGoal extends Goal> extends GoalCardLayoutProps {
    id: string;
    fieldConfigs: GoalFieldConfig<TGoal>[];
    fieldContainerFlexProps?: FlexProps;
    bottomContent?: JSX.Element;
    goalData: GoalData<TGoal>;
    updateField: (fieldName: string, fieldValue: unknown) => void;
    goBack: GoBackFcn;
    showSlideup: ShowSlideupFcn;
    isNextButtonDisabled?: (goalData: GoalData<TGoal>) => boolean;
}

const InputFieldCard = <TGoal extends Goal>({
    id,
    fieldConfigs,
    fieldContainerFlexProps,
    bottomContent,
    goalData,
    updateField,
    goBack,
    showSlideup,
    isNextButtonDisabled,
    ...props
}: InputFieldCardProps<TGoal>) => {
    const { userInfo } = useUserData();
    const firstInputRef = useAutofocus({ delay: 250 });
    const [isNextDisabled, setIsNextDisabled] = useState(true);

    useEffect(() => {
        setIsNextDisabled(
            isNextButtonDisabled
                ? isNextButtonDisabled(goalData)
                : fieldConfigs
                      .filter((c) => !c.isOptional)
                      .some(
                          (c) =>
                              goalData[c.fieldName] === '' ||
                              goalData[c.fieldName] === null ||
                              goalData[c.fieldName] === undefined
                      )
        );
    }, [fieldConfigs, goalData, isNextButtonDisabled]);

    if (!userInfo) return <></>;

    return (
        <GoalCardLayout {...props} isNextDisabled={isNextDisabled}>
            <FlexCol px={3} gap={4}>
                <FlexCol gap={3} {...fieldContainerFlexProps}>
                    {fieldConfigs.map((fieldConfig, i) => {
                        return (
                            <InputRow
                                key={id + fieldConfig.fieldName}
                                fieldConfig={fieldConfig}
                                goalData={goalData}
                                userInfo={userInfo}
                                updateField={updateField}
                                goBack={goBack}
                                showSlideup={showSlideup}
                                inputRef={
                                    i === 0 &&
                                    // avoid showing outline on first radio card option
                                    fieldConfig.fieldType !== 'radioCards'
                                        ? firstInputRef
                                        : undefined
                                }
                            />
                        );
                    })}
                </FlexCol>
                {bottomContent}
            </FlexCol>
        </GoalCardLayout>
    );
};

const AssumptionsCard = <TGoal extends Goal>({
    title,
    content,
    goalData,
    goTo,
    hideSlideup,
}: {
    title?: string;
    goalData: GoalData<TGoal>;
    content: (props: {
        goalData: GoalData<TGoal>;
        goTo: GoToFcn;
    }) => JSX.Element;
    goTo: GoToFcn;
    hideSlideup: HideSlideupFcn;
}) => {
    useKeyboardListener((key, e) => {
        if (key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            hideSlideup();
        }
    });
    const label = 'Back to goal';

    return (
        <FlexCol h="full" position="relative" bg="white">
            <FlexRow pt={6} px={5} justify="flex-end">
                <ButtonWithIcon
                    variant="ghost"
                    colorScheme="gray"
                    iconLeft={<ArrowDown size="sm" />}
                    onClick={() => {
                        hideSlideup();
                    }}
                    onClickEvent="Assumptions Card Close Button Clicked"
                    onClickEventParams={{ title, label }}
                    size="sm"
                    px={2}
                    h={10}
                >
                    {label}
                </ButtonWithIcon>
            </FlexRow>

            <FlexCol px={8} pb={8} pt={4}>
                <Subheader mb={4}>{title ?? 'Assumptions'}</Subheader>
                <FlexCol>{content({ goalData, goTo })}</FlexCol>
            </FlexCol>
        </FlexCol>
    );
};

const InputRow = <TGoal extends Goal>({
    fieldConfig,
    goalData,
    userInfo,
    updateField,
    goBack,
    showSlideup,
}: {
    fieldConfig: GoalFieldConfig<TGoal>;
    goalData: GoalData<TGoal>;
    userInfo: UserInfo;
    updateField: (fieldName: string, fieldValue: unknown) => void;
    goBack: GoBackFcn;
    showSlideup: ShowSlideupFcn;
    inputRef?: React.Ref<HTMLInputElement>;
}) => {
    const { fieldName, fieldType, fontSize } = fieldConfig;
    const fieldValue = goalData[fieldName];

    const min = 'min' in fieldConfig ? fieldConfig.min : undefined;
    const placeholder =
        'placeholder' in fieldConfig ? fieldConfig.placeholder : undefined;
    const fieldOptions =
        'fieldOptions' in fieldConfig
            ? typeof fieldConfig.fieldOptions === 'function'
                ? fieldConfig.fieldOptions({ goalData, userInfo })
                : fieldConfig.fieldOptions
            : undefined;
    const fieldLabel =
        'fieldLabel' in fieldConfig ? fieldConfig.fieldLabel : '';
    const bottomContent =
        'bottomContent' in fieldConfig
            ? typeof fieldConfig.bottomContent === 'function'
                ? fieldConfig.bottomContent({
                      goalData,
                      userInfo,
                      goBack,
                      showSlideup,
                  })
                : fieldConfig.bottomContent
            : undefined;

    const slider = useMemo(() => {
        return 'slider' in fieldConfig
            ? typeof fieldConfig.slider === 'function'
                ? fieldConfig.slider({ goalData })
                : fieldConfig.slider
            : undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const update = useCallback(
        (fieldName: string, v: any) => {
            updateField(
                fieldName,
                v === 'true' ? true : v === 'false' ? false : v
            );
        },
        [updateField]
    );

    const fieldValueDefault =
        'fieldValueDefault' in fieldConfig
            ? fieldConfig.fieldValueDefault
            : undefined;
    const defaultValue =
        typeof fieldValueDefault === 'function'
            ? fieldValueDefault({
                  goalData,
                  userInfo,
              })
            : fieldValueDefault;

    // If a default needs to be applied, call updateField to
    // store the form value and enable the Next button.
    useEffect(() => {
        if (!isDefined(fieldValue) && isDefined(defaultValue)) {
            update(fieldName, defaultValue);
        }
        // This works, but is "too complex" to represent.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
    }, [update, fieldName, fieldValue, defaultValue]);

    // HACK: This is gross - since we want the first render of InputField
    // to always have the default value, we early-exit here to allow
    // the update() call above to *set* that default value before
    // InputField is rendered for the first time.
    // Otherwise, partially-controlled components like radio cards won't
    // ingest the default value properly.
    if (!isDefined(fieldValue) && isDefined(defaultValue)) {
        return null;
    }

    // If selected option is not a valid option, clear the selection.
    // This can happen when the available options change.
    if (
        fieldType === 'select' &&
        !isEmpty(fieldValue) &&
        !fieldOptions?.find((option) => option.value === fieldValue)
    ) {
        updateField(fieldName, '');
    }

    // The radio card input expects a string, so convert if it's a boolean
    const inputValue =
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        typeof fieldValue === 'boolean' && fieldType === 'radioCards'
            ? String(fieldValue)
            : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              fieldValue;

    return (
        <FlexCol flexGrow={1}>
            <InputField
                name={fieldName}
                type={fieldType}
                label={fieldLabel}
                options={fieldOptions}
                onChange={(v) => update(fieldName, v)}
                value={inputValue}
                slider={slider}
                inputProps={{
                    variant: 'flushed',
                }}
                inputOptions={{ min }}
                radioProps={{
                    flexGrow: 1, // stretch to full width
                    size: 'lg',
                    fontSize,
                }}
                tabIndex={1}
                fontSize={fontSize}
                placeholder={placeholder}
                // TODO: Fix this. It's causing flickering issues and console errors.
                // ref={inputRef}
            />
            {bottomContent}
        </FlexCol>
    );
};
